import React, { useState } from "react";
import ProductListCell from "../../pages/products/productListCell";
import { pickProductListThumbUrl } from "../../pages/products/previewMedia";
import { creatorStudioName, effectiveStudioOwner } from "../../pages/products/productOwnership";
import { productAvailabilityLabel } from "../../pages/products/productStockDisplay";
import { PickerProduct } from "./productSelection";

export const DEFAULT_PRODUCT_FIELDS = [
  ["name", "Product Name"], ["productUniqueId", "HKP Product ID"], ["owner", "Product Owner"],
  ["creator", "Creator Organisation"], ["color", "Colour"], ["stock", "Stock / Commerce"],
  ["createdAt", "Created"], ["updatedAt", "Updated"],
];
export const OPTIONAL_PRODUCT_FIELDS = [
  ["xuid", "XUID / NFC"], ["artform", "Artform"], ["type", "Type"], ["category", "Category"],
  ["subcategory", "Sub-category"], ["weavetype", "Weave Type"], ["hsnCode", "HSN"],
];
export function productField(product: PickerProduct, field: string): string {
  if (field === "owner") return effectiveStudioOwner(product.productOwner, creatorStudioName(product)) || "—";
  if (field === "creator") return creatorStudioName(product) || "—";
  if (field === "stock") return product.commerceState === "SOLD" ? "Sold" : product.commerceState === "SELECTED_FOR_BILLING" ? "On a billing memo" : productAvailabilityLabel(product);
  const value = product[field];
  if (field === "createdAt" || field === "updatedAt") {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : "—";
  }
  return String((typeof value === "object" ? value?.name : value) || "—");
}
export function ProductThumbnail({ product }: { product: PickerProduct }) {
  const url = pickProductListThumbUrl(product.thumbnailDetails?.media) || pickProductListThumbUrl(product.thumbnail ? [{ mediaURL: product.thumbnail }] : []);
  const [failed, setFailed] = useState("");
  return url && failed !== url ? <img className="hkp-product-thumbnail" src={url} alt={product.name || "Product"} onError={() => setFailed(url)} />
    : <span className="hkp-product-thumbnail logoPreview" role="img" aria-label="No product image">No Image</span>;
}

type Props = { product: PickerProduct; onView?: () => void; fields?: string[]; table?: boolean; reason?: string; renderField?: (field: string) => React.ReactNode };
export default function HKPProductSummary({ product, onView, fields, table, reason, renderField }: Props) {
  const image = onView ? <button type="button" className="hkp-product-view-link" onClick={onView} aria-label={`View ${product.name || product.productUniqueId}`}><ProductThumbnail product={product} /></button> : <ProductThumbnail product={product} />;
  const name = onView ? <button type="button" className="hkp-product-view-link" onClick={onView}>{product.name || product.productUniqueId}</button> : <strong>{product.name || product.productUniqueId}</strong>;
  if (table) return <><td>{image}</td>{fields?.map((field) => <td key={field}>
    {field === "name" ? <>{name}{reason && <small id={`reason-${product.productUniqueId}`} className="hkp-product-reason">{reason}</small>}</> : renderField?.(field) ?? <ProductListCell value={productField(product, field)} preserveCase />}
  </td>)}</>;
  return <div className="hkp-product-summary">
    <div className="hkp-product-summary__identity">{image}<div>{name}<div>{product.productUniqueId}</div>{reason && <small id={`reason-${product.productUniqueId}`} className="hkp-product-reason">{reason}</small>}</div></div>
    <dl>{(fields || ["owner", "creator", "color", "type", "stock", "createdAt", "updatedAt"]).map((field) => <div key={field}>
      <dt>{[...DEFAULT_PRODUCT_FIELDS, ...OPTIONAL_PRODUCT_FIELDS].find(([key]) => key === field)?.[1]}</dt><dd>{productField(product, field)}</dd>
    </div>)}</dl>
  </div>;
}
